import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import jwtDecode from 'jwt-decode';
import { withTranslation } from 'react-i18next';
import { useOnlineState } from 'beautiful-react-hooks';

import { makeGetMasterToken, makeGetTeamUrl, makeGetTeamToken, makeGetLoggingOut, makeGetSigningUp } from '../selectors/auth';
import { setTeamToken, setAuthDetails } from '../actions/auth';

import AjaxService from '../services/ajax-service';
import { createAlert } from '../services/alert-service';
import { debugLog as log } from 'shared-services/logger-service';
import { getTokenParam } from '../utils';

import Main from 'lazy/LazyMain';

import AuthFlow from '../components/auth-flow';
import AppLoading from '../components/app-loading';
import UserLinkSSO from '../components/user-link-sso';
import TeamTFARequired from '../components/TeamTFARequired';

const Gate = ({ masterToken, teamUrl, teamToken, loggingOut, signingUp, children, t }) => {
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showLogin, setShowLogin] = useState(false);
    const [showLinkSSO, setShowLinkSSO] = useState(false);
    const [showSetTFA, setShowSetTFA] = useState(false);
    const isOnline = useOnlineState();

    const showAlert = (type, text) => {
        createAlert(type, text);
    }

    const showAccessAlert = reason => {
        switch (reason) {
            case 'NotFound':
                showAlert('default', t('access.notFound'));
                break;

            case 'NotJoined':
                showAlert('default', t('access.notJoined'));
                break;

            case 'Removed':
                showAlert('error', t('access.removed'));
                break;

            default:
                // es lint
        }
    }

    const handleAccessError = (reason, additionalInfo) => {
        setHasAccess(false);
        setLoading(false);

        if (reason) {
            if (reason === 'SSO') {
                setShowLinkSSO(additionalInfo);
                return;
            }

            if (reason === 'TFA') {
                setShowSetTFA(true);
                return;
            }

            setTimeout(() => {
                showAccessAlert(reason);
            }, 1000);
        }

        setShowLogin(true);
    }

    const compareTokens = (prevToken, nextToken) => {
        if (!prevToken) {
            return false;
        }

        const prevTokenProps = jwtDecode(prevToken);
        const nextTokenProps = jwtDecode(nextToken);

        if (prevTokenProps.exp * 1000 < new Date()) {
            return false;
        }

        if (prevTokenProps.team_id !== nextTokenProps.team_id) {
            return false;
        }

        if (prevTokenProps.member_id !== nextTokenProps.member_id) {
            return false;
        }

        return true;
    }

    const testAccess = async (masterToken, teamUrl, teamToken) => {
        setShowLogin(false);
        setShowLinkSSO(false);
        setShowSetTFA(false);

        try {
            const accessInfoResponse = await AjaxService({
                method: 'get',
                url: `teams/access/${teamUrl}`
            }, false, masterToken);

            const accessInfo = accessInfoResponse?.data;

            if (accessInfo.reason) {
                return handleAccessError(accessInfo.reason, accessInfo.additional_info);
            }

            if (!compareTokens(teamToken, accessInfo.token)) {
                await setTeamToken(teamUrl, accessInfo.token);
            }

            setHasAccess(Boolean(accessInfo.has_access));
            setLoading(false);
        } catch (e) {
            handleAccessError();
        }
    }

    const handleTFASet = () => {
        testAccess(masterToken, teamUrl, teamToken);
    }

    const handleSSOCancel = () => {
        setAuthDetails({
            teamUrl: null,
            masterToken: null,
            teamToken: null
        });

        testAccess(null, null, null);
    }

    useEffect(() => {
        if (!masterToken || !teamUrl) {
            setHasAccess(false);
            setShowLogin(true);
            setLoading(false);
            return;
        }

        if (isOnline) {
            log('[AUTH GATE] Testing access...');
            testAccess(masterToken, teamUrl, teamToken);
        }
    }, [masterToken, teamUrl]);

    useEffect(() => {
        log('[AUTH GATE] Master token change');
    }, [masterToken]);

    useEffect(() => {
        log('[AUTH GATE] Team URL change');
    }, [teamUrl]);

    if (loggingOut) {
        return null;
    }

    if (loading) {
        return <AppLoading id="gate" showQuote={false} timeout={10000} />;
    }

    if (showLinkSSO) {
        return <UserLinkSSO ssoPolicy={showLinkSSO} onCancel={handleSSOCancel} />;
    }

    if (showSetTFA) {
        return <TeamTFARequired authToken={masterToken} email={getTokenParam(masterToken, 'email')} onSuccess={handleTFASet} />;
    }

    if (showLogin) {
        return <AuthFlow />;
    }

    if (hasAccess) {
        return <Main>{children}</Main>;
    }

    return <AuthFlow />;
}

const makeMapStateToProps = () => {
    const getMasterToken = makeGetMasterToken();
    const getTeamUrl = makeGetTeamUrl();
    const getTeamToken = makeGetTeamToken();
    const getLoggingOut = makeGetLoggingOut();
    const getSigningUp = makeGetSigningUp();

    return ({ auth }) => ({
        masterToken: getMasterToken(auth),
        teamUrl: getTeamUrl(auth),
        teamToken: getTeamToken(auth),
        loggingOut: getLoggingOut(auth),
        signingUp: getSigningUp(auth)
    });
}

export default connect(makeMapStateToProps)(withTranslation('errors')(Gate));
